import React from 'react';
import {Link} from 'react-router';
import type {ClassroomModelV1, SubjectModelV2} from '@albert-io/models';
import {history} from 'client/history';
import withTracking from 'lib/hocs/withTracking';
import classnames from 'classnames';
import {Heading, Text, Button} from '@albert-io/atomic';
import SubjectSplitCard from 'client/components/SubjectSplitCard/SubjectSplitCard.react';
import './teacher-subjects-list.scss';
import 'client/components/SubjectSplitCard/subject-split-card.scss';

const TrackedOpenSubjectSplitCard = withTracking(SubjectSplitCard.Bottom, {
  onClick: {
    event: 'click',
    type: 'button',
    feature: 'Class Subjects in Classroom',
    component: 'Subject Card',
    content: 'Open access'
  }
});

const TrackedCloseSubjectSplitCard = withTracking(SubjectSplitCard.Bottom, {
  onClick: {
    event: 'click',
    type: 'button',
    feature: 'Class Subjects in Classroom',
    component: 'Subject Card',
    content: 'Close access'
  }
});

const TrackedLink = withTracking(Link, {
  onClick: {
    event: 'click',
    type: 'button',
    feature: 'Class Subjects Tab',
    component: 'Subject Card',
    content: (e) => e.target.dataset.trackingSubject
  }
});

interface Props {
  subject: SubjectModelV2;
  classroom: ClassroomModelV1;
  showActions: boolean;
  handleCloseAccess: (subject: SubjectModelV2) => void;
  handleOpenAccess: (subject: SubjectModelV2) => void;
}

export const SubjectsListCard = ({
  subject,
  classroom,
  showActions,
  handleCloseAccess,
  handleOpenAccess
}: Props) => {
  const isHidden = subject.isHidden();
  const makeSubjectCardHref = () => `/${subject.getUrlSlug()}`;

  const renderCardActions = () => {
    const licensedSubjects = classroom.getMeta().getSchoolLicensedSubjects();
    const groupLicensedSubjects = licensedSubjects.get('subject_group_subject_ids');
    const standaloneLicensedSubjects = licensedSubjects.get(
      'standalone_school_license_subject_ids'
    );
    const isSubjectLicensed =
      groupLicensedSubjects.includes(subject.getId()) ||
      standaloneLicensedSubjects.includes(subject.getId());
    const isAssessmentsOnly =
      !subject.getGuides().isEmpty() &&
      subject.getGuides().every((guide) => {
        return guide.getGuideType() === 'assessment';
      });
    const isRestricted = subject
      .getClassroomRelationships(classroom.getId())
      .getRestrictFreePractice();

    if (isHidden) {
      return (
        <SubjectSplitCard.Bottom
          paddingLevel={0}
          border='none'
          className='teacher-subject-list__card__btn subject-split-card__action-bar u-display_flex u-justify-content_flex-end u-text-align_right u-pad-lr_3 u-pad-tb_1 u-color_slate-400 u-font-style_italic u-pointer-events_none'
          variant='text'
          as='div'
        >
          <Text italic emphasis='high' size='m'>
            Hidden Subject
          </Text>
        </SubjectSplitCard.Bottom>
      );
    }
    if (isAssessmentsOnly) {
      return (
        <SubjectSplitCard.Bottom
          paddingLevel={0}
          border='none'
          className='teacher-subject-list__card__btn subject-split-card__action-bar u-display_flex u-justify-content_flex-end u-text-align_right u-pad-lr_3 u-pad-tb_1 u-color_slate-400 u-font-style_italic u-pointer-events_none'
          variant='text'
          as='div'
        >
          <Text italic emphasis='high' size='m'>
            Practice settings not available
          </Text>
        </SubjectSplitCard.Bottom>
      );
    }
    if (!isSubjectLicensed) {
      return (
        <SubjectSplitCard.Bottom
          paddingLevel={0}
          onClick={() => history.push('/account/access')}
          border='none'
          className='teacher-subject-list__card__btn subject-split-card__action-bar u-display_flex u-justify-content_flex-end'
          transparentBg
          as='div'
        >
          <Text italic emphasis='high' size='m' className='u-mar-t_1 u-mar-r_1'>
            <Link to='/account/access' className='u-color_slate-500'>
              Needs License
            </Link>
          </Text>
        </SubjectSplitCard.Bottom>
      );
    }
    if (isRestricted) {
      return (
        <TrackedOpenSubjectSplitCard
          paddingLevel={0}
          border='none'
          className='teacher-subject-list__card__btn subject-split-card__action-bar u-display_flex u-justify-content_flex-end u-text-align_right u-pad-lr_3 u-pad-tb_1 u-font-weight_bold'
          as={Button}
          onClick={() => handleOpenAccess(subject)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleOpenAccess(subject);
            }
          }}
          role='switch'
          variant='text'
          color='green'
        >
          Open access
        </TrackedOpenSubjectSplitCard>
      );
    }
    if (!isAssessmentsOnly && !isRestricted) {
      return (
        <TrackedCloseSubjectSplitCard
          paddingLevel={0}
          border='none'
          className='teacher-subject-list__card__btn subject-split-card__action-bar u-display_flex u-justify-content_flex-end u-text-align_right u-pad-lr_3 u-pad-tb_1 u-font-weight_bold'
          as={Button}
          onClick={() => handleCloseAccess(subject)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleCloseAccess(subject);
            }
          }}
          role='button'
          variant='text'
          color='destructive'
        >
          Close access
        </TrackedCloseSubjectSplitCard>
      );
    }
    return null;
  };

  const SubjectSplitCardTopWrapper = isHidden ? 'div' : TrackedLink;

  return (
    <SubjectSplitCard
      className='subject-split-card--action-bar-in-view teacher-subjects-list__course u-text-decoration_none'
      key={subject.getId()}
    >
      <SubjectSplitCardTopWrapper
        to={makeSubjectCardHref()}
        className='u-text-decoration_none'
        data-tracking-subject={subject.getName()}
      >
        <SubjectSplitCard.Top
          className={classnames(
            'teacher-subject-list__card__link u-display_flex u-align-items_flex-end u-pad-lr_3 u-pad-tb_3 u-color_white u-overflow_hidden u-text-decoration_none',
            {
              [`u-bgc_${subject
                .getCurriculumArea()
                .getName()
                .toLowerCase()
                .replace(' ', '-')}-500`]: !isHidden && !subject.getCurriculumArea().isEmpty(),
              'u-bgc_blue-500': !isHidden && subject.getCurriculumArea().isEmpty(),
              'teacher-subject-list__card__link--hidden': isHidden
            }
          )}
        >
          <SubjectSplitCard.Thumbnail xlinkHref={subject.getLogoLocation().href} />
          <SubjectSplitCard.Title className='u-display_flex u-flex-direction_column'>
            <Heading className='teacher-subject-list__card__link__title'>
              {subject.getName()}
            </Heading>
          </SubjectSplitCard.Title>
        </SubjectSplitCard.Top>
      </SubjectSplitCardTopWrapper>
      {showActions && renderCardActions()}
    </SubjectSplitCard>
  );
};
